// src/pages/funnels/LeadsTable.jsx
// Leads tab: paginated lead list + CSV export + delete.
import { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../../lib/authFetch';
import {
  Button, Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '../../components/ui';
import { formatRelativeTime } from '../../lib/utils';
import { Download, Trash2, Loader2, Users } from 'lucide-react';
import toast from 'react-hot-toast';

const PAGE_SIZE = 50;

export default function LeadsTable({ funnelId, funnelName }) {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const loadLeads = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(
        `/api/funnels?resource=leads&funnelId=${funnelId}&limit=${PAGE_SIZE}&offset=${page * PAGE_SIZE}`
      );
      const data = await res.json();
      if (data.success) {
        setLeads(data.leads || []);
        setTotal(data.total || 0);
      }
    } catch (e) {
      console.error('Leads load error:', e);
    } finally {
      setLoading(false);
    }
  }, [funnelId, page]);

  useEffect(() => { loadLeads(); }, [loadLeads]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await authFetch(`/api/funnels?resource=leads&funnelId=${funnelId}&format=csv`);
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(funnelName || 'funnel').toLowerCase().replace(/[^a-z0-9]+/g, '-')}-leads.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async (lead) => {
    if (!window.confirm(`Delete lead ${lead.email}?`)) return;
    try {
      const res = await authFetch('/api/funnels?resource=leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'delete', leadId: lead.id }),
      });
      const data = await res.json();
      if (!data.success) return toast.error(data.error || 'Failed to delete');
      setLeads(prev => prev.filter(l => l.id !== lead.id));
      setTotal(t => Math.max(0, t - 1));
    } catch {
      toast.error('Failed to delete');
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {total} lead{total === 1 ? '' : 's'} captured
        </p>
        <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting || total === 0}>
          {exporting
            ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            : <Download className="h-3.5 w-3.5 mr-1.5" />}
          Export CSV
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : leads.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center border border-dashed border-border rounded-lg">
          <Users className="h-8 w-8 text-muted-foreground/40 mb-2" />
          <p className="text-sm text-muted-foreground max-w-xs">
            No leads yet. When visitors submit the opt-in form on your published funnel, they'll show up here
            (and in your Email Marketing contacts).
          </p>
        </div>
      ) : (
        <>
          <div className="rounded-lg border border-border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Step</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Captured</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.map(lead => (
                  <TableRow key={lead.id}>
                    <TableCell className="font-medium">{lead.email}</TableCell>
                    <TableCell>{lead.name || '—'}</TableCell>
                    <TableCell className="text-muted-foreground">{lead.funnel_steps?.name || '—'}</TableCell>
                    <TableCell className="text-muted-foreground text-xs">
                      {lead.utm_source
                        ? [lead.utm_source, lead.utm_medium].filter(Boolean).join(' / ')
                        : 'direct'}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs whitespace-nowrap">
                      {formatRelativeTime(lead.created_at)}
                    </TableCell>
                    <TableCell>
                      <button
                        onClick={() => handleDelete(lead)}
                        className="p-1.5 hover:bg-muted rounded text-muted-foreground hover:text-destructive"
                        title="Delete lead"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-end gap-2 text-sm">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                Previous
              </Button>
              <span className="text-muted-foreground text-xs">
                Page {page + 1} of {totalPages}
              </span>
              <Button variant="outline" size="sm" disabled={page + 1 >= totalPages} onClick={() => setPage(p => p + 1)}>
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
